/**
 * The paper register's two sheets: the record (or one version of it) and one
 * commitment. Read-deep, never live (spec §4 Move 7): every line on the record
 * sheet is an address to more paper, all frozen at the same version. Nothing
 * here filters, edits, or refreshes.
 */

import { Link } from 'react-router-dom';
import { POR_SECTION_ORDER, type PorCommitment, type PorCommitmentStatus, type PorGovernanceEvent } from '../../types/plan-of-record';
import { recordAddress, withShareToken, type SharedRecordRead } from '../../lib/porShareService';
import { ASK_DECISION_TYPE_LABELS } from '../../components/plan-of-record/porUi';

// ── Small helpers ─────────────────────────────────────────────────────────────

function paperDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

const STATUS_ORDER: PorCommitmentStatus[] = ['active', 'next', 'blocked', 'watch', 'complete'];
const STATUS_LABEL: Record<PorCommitmentStatus, string> = {
  active: 'Active now',
  next: 'Up next',
  blocked: 'Blocked',
  watch: 'Watching',
  complete: 'Complete',
};
const STATUS_TAG: Partial<Record<PorCommitmentStatus, string>> = {
  blocked: 'record-paper__tag--danger',
  complete: 'record-paper__tag--ok',
  watch: 'record-paper__tag--warn',
};
const TYPE_LABEL: Record<PorCommitment['type'], string> = {
  hard: 'Hard commitment',
  soft: 'Soft commitment',
  aspirational: 'Aspiration',
};

function Tag({ children, tone }: { children: React.ReactNode; tone?: string }) {
  return <span className={`record-paper__tag ${tone ?? ''}`}>{children}</span>;
}

/** Governance events that mention a commitment, by id, in either value blob. */
function eventsMentioning(events: PorGovernanceEvent[], commitmentId: string): PorGovernanceEvent[] {
  return events.filter((e) => {
    try {
      return JSON.stringify(e.previous_value ?? {}).includes(commitmentId) || JSON.stringify(e.new_value ?? {}).includes(commitmentId);
    } catch {
      return false;
    }
  });
}

// ── Seal ──────────────────────────────────────────────────────────────────────

export function Seal({ data }: { data: SharedRecordRead }) {
  const { snapshot } = data;
  const provenance: string[] = [];
  if (snapshot.hasCycleReport) provenance.push('the ratified priority list');
  if (snapshot.hasPlanVersion) provenance.push('the committed plan version');
  return (
    <div className="record-paper__seal">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <span>
          <strong style={{ color: 'var(--paper-ink)' }}>Ratified record · v{snapshot.version}</strong>
          {' · '}ratified {paperDate(snapshot.authorizedAt)} under the authority of <strong style={{ color: 'var(--paper-ink)' }}>{snapshot.ratificationAuthority}</strong>
        </span>
        {snapshot.superseded ? (
          <Tag tone="record-paper__tag--warn">Superseded — historical version</Tag>
        ) : (
          <Tag tone="record-paper__tag--ok">Record of authority</Tag>
        )}
      </div>
      <p className="mt-1.5" style={{ fontSize: '0.8125rem' }}>
        <em>Rationale.</em> {snapshot.rationale}
        {provenance.length > 0 ? <> Frozen with {provenance.join(' and ')}.</> : null}
      </p>
    </div>
  );
}

// ── The record sheet ──────────────────────────────────────────────────────────

export function RecordSheet({ data, token }: { data: SharedRecordRead; token: string | null }) {
  const payload = data.payload;
  const porId = data.record.id;
  const v = data.snapshot.version;
  const address = (parts: { commitmentId?: string }) =>
    withShareToken(recordAddress(porId, { version: v, ...parts }), token);

  if (!payload) return null;
  const commitments = [...(payload.commitments ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const groups = STATUS_ORDER.map((status) => ({ status, items: commitments.filter((c) => c.status === status) })).filter(
    (g) => g.items.length > 0,
  );
  const sections = POR_SECTION_ORDER.map((t) => (payload.sections ?? []).find((s) => s.section_type === t)).filter(
    (s): s is NonNullable<typeof s> => Boolean(s && s.body && s.body.trim()),
  );
  const primaryKpis = (payload.kpis ?? []).filter((k) => k.tier === 'primary');
  const openRisks = (payload.risks ?? []).filter((r) => r.status !== 'resolved');
  const openAsks = (payload.executive_asks ?? []).filter((a) => a.status === 'open');
  const events = (payload.governance_events ?? []).slice(0, 12);
  let rank = 0;

  return (
    <article className="record-paper__sheet">
      <header>
        <div className="record-paper__eyebrow">
          {data.record.workspaceName ? `${data.record.workspaceName} · ` : ''}Plan of Record
        </div>
        <h1 className="record-paper__title">{data.record.title}</h1>
        <div className="record-paper__meta mt-2">
          Condition {payload.record?.current_condition ?? '—'}
          {payload.record?.condition_rationale ? <> — {payload.record.condition_rationale}</> : null}
          {payload.record?.horizon_short || payload.record?.horizon_long ? (
            <> · Horizon {payload.record.horizon_short ?? '—'} / {payload.record.horizon_long ?? '—'}</>
          ) : null}
        </div>
      </header>

      <Seal data={data} />

      <h2>Commitments</h2>
      {groups.length === 0 ? (
        <p className="record-paper__note">No commitments were named on this version.</p>
      ) : (
        groups.map((g) => (
          <section key={g.status} className="mb-4">
            <div className="record-paper__meta mb-1" style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {STATUS_LABEL[g.status]} · {g.items.length}
            </div>
            <div>
              {g.items.map((c) => {
                rank += 1;
                return (
                  <Link key={c.id} to={address({ commitmentId: c.id })} className="record-paper__row" title="Open this commitment's own page">
                    <span className="record-paper__rank">{String(rank).padStart(2, '0')}</span>
                    <span className="min-w-0 flex-1">
                      <div className="record-paper__rowtitle">{c.title}</div>
                      <div className="record-paper__meta flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span>{TYPE_LABEL[c.type]}</span>
                        {c.target_window_label ? <span>· {c.target_window_label}</span> : null}
                        {c.status === 'blocked' ? <Tag tone={STATUS_TAG.blocked}>Blocked</Tag> : null}
                      </div>
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        ))
      )}

      {sections.length > 0 && (
        <>
          <h2>The narrative</h2>
          {sections.map((s) => (
            <section key={s.id} className="mb-6">
              <h3 className="text-lg" style={{ fontWeight: 500 }}>{s.title}</h3>
              <p className="record-paper__body mt-1">{s.body}</p>
            </section>
          ))}
        </>
      )}

      {primaryKpis.length > 0 && (
        <>
          <h2>Primary measures</h2>
          <div className="record-paper__kpis">
            {primaryKpis.map((k) => (
              <div key={k.id}>
                <div className="record-paper__kpi-value">{k.has_data ? (k.value_display ?? k.value_string ?? k.value_raw ?? '—') : 'No data'}</div>
                <div className="record-paper__meta">
                  {k.label}
                  {k.target_label ? <> · target {k.target_label}</> : null}
                  {k.target_window ? <> ({k.target_window})</> : null}
                </div>
              </div>
            ))}
          </div>
          <p className="record-paper__note mt-2">No data is not zero.</p>
        </>
      )}

      {openRisks.length > 0 && (
        <>
          <h2>Risks on the record</h2>
          {openRisks.map((r) => (
            <div key={r.id} className="record-paper__row">
              <span className="record-paper__rank" style={{ color: r.severity === 'high' ? 'var(--paper-danger)' : 'var(--paper-ink-3)' }}>
                {r.severity.slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <div className="record-paper__rowtitle">{r.description}</div>
                <div className="record-paper__meta">Mitigation — {r.mitigation}</div>
              </span>
            </div>
          ))}
        </>
      )}

      {openAsks.length > 0 && (
        <>
          <h2>Open asks of leadership</h2>
          {openAsks.map((a) => (
            <div key={a.id} className="record-paper__row">
              <span className="record-paper__rank">{String(a.rank).padStart(2, '0')}</span>
              <span className="min-w-0 flex-1">
                <div className="record-paper__rowtitle">{a.description}</div>
                <div className="record-paper__meta">
                  {ASK_DECISION_TYPE_LABELS[a.decision_type] ?? a.decision_type} · {a.rationale}
                  {a.due_by ? <> · due {paperDate(a.due_by)}</> : null}
                </div>
              </span>
            </div>
          ))}
        </>
      )}

      {events.length > 0 && (
        <>
          <h2>Change log</h2>
          {events.map((e) => (
            <div key={e.id} className="record-paper__row">
              <span className="record-paper__rank" style={{ color: 'var(--paper-ink-3)', width: '7.5rem' }}>
                {new Date(e.created_at).toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: '2-digit' })}
              </span>
              <span className="min-w-0 flex-1">
                <div style={{ fontSize: '0.9375rem' }}>{e.summary ?? e.event_type.replace(/_/g, ' ')}</div>
                {e.rationale ? <div className="record-paper__meta">{e.rationale}</div> : null}
              </span>
            </div>
          ))}
        </>
      )}

      <footer className="record-paper__footer">
        This page is a frozen ratified record. It does not change; a newer version, if one exists, is a separate page at the same address.
        {data.versions.length > 1 && data.scope === 'record' ? (
          <span className="record-paper__noprint">
            {' '}Versions:{' '}
            {data.versions.map((ver, i) => (
              <span key={ver.version}>
                {i > 0 ? ', ' : ''}
                {ver.version === v ? (
                  <strong>v{ver.version}</strong>
                ) : (
                  <Link to={withShareToken(recordAddress(porId, { version: ver.version }), token)} style={{ textDecoration: 'underline' }}>
                    v{ver.version}
                  </Link>
                )}
              </span>
            ))}
            .
          </span>
        ) : null}
      </footer>
    </article>
  );
}

// ── The commitment sheet ──────────────────────────────────────────────────────

export function CommitmentSheet({ data, token }: { data: SharedRecordRead; token: string | null }) {
  const c = data.commitment;
  if (!c) return null;
  const porId = data.record.id;
  const v = data.snapshot.version;
  const canOpenRecord = data.scope !== 'commitment';
  const history = data.payload ? eventsMentioning(data.payload.governance_events ?? [], c.id) : [];

  return (
    <article className="record-paper__sheet">
      <header>
        <div className="record-paper__eyebrow">
          {data.record.workspaceName ? `${data.record.workspaceName} · ` : ''}
          {canOpenRecord ? (
            <Link to={withShareToken(recordAddress(porId, { version: v }), token)} style={{ textDecoration: 'underline' }}>
              {data.record.title}
            </Link>
          ) : (
            data.record.title
          )}
          {' · '}commitment
        </div>
        <h1 className="record-paper__title">{c.title}</h1>
        <div className="record-paper__meta mt-2 flex flex-wrap items-center gap-2">
          <Tag>{TYPE_LABEL[c.type]}</Tag>
          <Tag tone={STATUS_TAG[c.status]}>{STATUS_LABEL[c.status]}</Tag>
          {c.target_window_label ? <span>· {c.target_window_label}</span> : null}
        </div>
      </header>

      <Seal data={data} />

      {c.description ? (
        <>
          <h2>What was committed</h2>
          <p className="record-paper__body">{c.description}</p>
        </>
      ) : (
        <p className="record-paper__note">No description was recorded for this commitment.</p>
      )}

      {c.status === 'blocked' && c.blocker_description ? (
        <>
          <h2>Why it is blocked</h2>
          <p className="record-paper__body">{c.blocker_description}</p>
        </>
      ) : null}

      {data.linkedSignalEvents.length > 0 && (
        <>
          <h2>Signals on this commitment</h2>
          {data.linkedSignalEvents.map((s) => (
            <div key={s.id} className="record-paper__row">
              <span className="record-paper__rank">{s.condition}</span>
              <span className="min-w-0 flex-1">
                <div className="record-paper__rowtitle">{s.description}</div>
                <div className="record-paper__meta">
                  {s.rationale}
                  {s.action_required ? <> · Action: {s.action_required}</> : null}
                  {s.resolved_at ? <> · resolved {paperDate(s.resolved_at)}</> : null}
                </div>
              </span>
            </div>
          ))}
        </>
      )}

      {history.length > 0 && (
        <>
          <h2>History on the record</h2>
          {history.map((e) => (
            <div key={e.id} className="record-paper__row">
              <span className="record-paper__rank" style={{ color: 'var(--paper-ink-3)', width: '7.5rem' }}>
                {new Date(e.created_at).toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: '2-digit' })}
              </span>
              <span className="min-w-0 flex-1">
                <div style={{ fontSize: '0.9375rem' }}>{e.summary ?? e.event_type.replace(/_/g, ' ')}</div>
                {e.rationale ? <div className="record-paper__meta">{e.rationale}</div> : null}
              </span>
            </div>
          ))}
        </>
      )}

      <footer className="record-paper__footer">
        Part of ratified record v{v}, ratified {paperDate(data.snapshot.authorizedAt)}. This page is frozen at that version.
      </footer>
    </article>
  );
}
